import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import styled from 'styled-components';
import { graphQLDatabaseIdIntDecoder } from 'helpers/graphql-utils';
import CCModal from 'components/shared/DesignSystem/CCModal/CCModal';
import AssetFeed from 'components/assetFeed/AssetFeed';
import PickerFooter from './PickerFooter';
import { FILTER_PHOTOS_ONLY } from 'constants/filters';
import { translate } from 'javascripts/translations/i18n';

// #region styled-components
const Container = styled.div`
  padding: var(--cc_size_spacing_l);
`;
const AssetFeedContainer = styled.div`
  padding: 0 var(--cc_size_spacing_xl);
`;
// #endregion

type Asset = {
  databaseId: number;
  timestamp: string;
};

export type PageAssetPickerModalRef = {
  pickPhotos: (count: number) => Promise<Asset[] | null>;
};

export type PageAssetPickerModalProps = {
  projectNodeId: string;
  onRequestClose?: () => void;
};

export const PagePhotoPickerModal = forwardRef<
  PageAssetPickerModalRef,
  PageAssetPickerModalProps
>(({ projectNodeId, onRequestClose = () => {} }, ref) => {
  const projectId = graphQLDatabaseIdIntDecoder(projectNodeId);

  const [isOpen, setIsOpen] = useState(false);
  const [maxSelectionCount, setMaxSelectionCount] = useState(0);
  const [selectionPromise, setSelectionPromise] = useState<
    ((assets: Asset[]) => void) | null
  >(null);

  useImperativeHandle(ref, () => ({
    pickPhotos: (count) => {
      setIsOpen(true);
      setMaxSelectionCount(count);
      return new Promise<Asset[] | null>((resolve) => {
        setSelectionPromise(() => resolve);
      });
    },
  }));

  const assetFilters: any = {
    assetType: FILTER_PHOTOS_ONLY,
    capturedAfter: null,
    capturedBefore: null,
    projects: [],
    users: [],
    groups: [],
    tags: [],
  };

  const handleSelection = (photos: Asset[]) => {
    if (selectionPromise) {
      selectionPromise(photos);
      setSelectionPromise(null);
    }
    setIsOpen(false);
    onRequestClose();
  };

  const handleCancel = () => {
    if (selectionPromise) {
      selectionPromise([]);
      setSelectionPromise(null);
    }
    setIsOpen(false);
    onRequestClose();
  };

  useEffect(() => {
    return () => {
      if (selectionPromise) {
        selectionPromise([]);
        setSelectionPromise(null);
      }
    };
  }, [selectionPromise]);

  // TODO: Use slab's modal after moving page out of modal
  // Slab's modal is conflicting with the page modal.
  return (
    <CCModal
      isOpen={isOpen}
      onRequestClose={handleCancel}
      size="large"
      headerStyle={{ borderBottomWidth: 0 }}
      title={translate('projectPage.photoPicker.title')}
    >
      <Container>
        <AssetFeedContainer>
          <AssetFeed
            projectDatabaseId={projectId}
            entityId={projectNodeId}
            maxSelectionCount={maxSelectionCount}
            multiSelectHandlerComponent={
              <PickerFooter
                // @ts-expect-error
                onSelection={handleSelection}
                onCancel={handleCancel}
              />
            }
            isPresentedInModal
            initialFilters={assetFilters}
          />
        </AssetFeedContainer>
      </Container>
    </CCModal>
  );
});
